'use client';
import { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import Link from 'next/link';
import { ArrowLeft, Copy, Check, Globe, Download, Loader2 } from 'lucide-react';

export default function UrlImporterPage() {
  const [url, setUrl] = useState('');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const fetchUrl = async () => {
    try {
      setError('');
      if (!url.trim()) {
        setError('Please enter a URL');
        return;
      }

      setLoading(true);
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
      }

      const text = await response.text();
      setOutput(text);
    } catch (err: any) {
      setError(err.message || 'Error fetching URL. This might be due to CORS restrictions on the target server.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadCsv = () => {
    const blob = new Blob([output], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'imported_data.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <div className="marketing-theme tool-page">

      <Navbar />

      <main className="tool-page-main flex flex-col items-center">
        <div className="w-full max-w-5xl">
          <Link 
            href="/tools" 
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Link>

          <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                URL Data Importer
              </h1>
            <p className="text-lg text-muted-foreground">
              Import CSV or text data directly from a public URL
            </p>
          </div>

          <div className="mb-8 max-w-2xl mx-auto">
            <label className="text-sm font-medium mb-2 block">Enter Public CSV/Text URL</label>
            <div className="flex gap-2">
              <Input 
                placeholder="https://example.com/data.csv" 
                value={url} 
                onChange={(e) => setUrl(e.target.value)}
                className="flex-1"
              />
              <Button onClick={fetchUrl} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Globe className="w-4 h-4 mr-2" />}
                Import
              </Button>
            </div>
            {error && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
                <p className="font-semibold mb-1">Import Error:</p>
                <p>{error}</p>
                <p className="mt-2 text-xs opacity-80 italic">Note: Many websites block direct browser requests (CORS). Try using a direct link to a raw file (e.g., from GitHub Gist or a public S3 bucket).</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Imported Content</label>
              <Textarea
                value={output}
                readOnly
                placeholder="Imported data will appear here..."
                className="min-h-[400px] font-mono text-sm bg-muted"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={copyToClipboard} 
                className="flex-1"
                variant="outline"
                disabled={!output}
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </Button>
              <Button 
                onClick={downloadCsv} 
                className="flex-1"
                variant="outline"
                disabled={!output}
              >
                <Download className="w-4 h-4 mr-2" />
                Download as CSV
              </Button>
            </div>
          </div>
        </div>
      </main>

      <section className="max-w-4xl mx-auto mt-16 mb-12 p-6 bg-card rounded-lg border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold mb-4">URL Importer FAQs</h2>
        <div className="space-y-6">
          <div><p className="font-semibold">1. Why did my import fail with a CORS error?</p><p className="text-muted-foreground">CORS (Cross-Origin Resource Sharing) is a security feature that prevents websites from fetching data from other domains unless explicitly allowed. Many servers block these requests for security.</p></div>
          <div><p className="font-semibold">2. What kind of URLs work best?</p><p className="text-muted-foreground">Direct links to raw files on GitHub, public Google Sheets (published to web as CSV), or files on servers with permissive CORS headers work best.</p></div>
          <div><p className="font-semibold">3. Is my data secure?</p><p className="text-muted-foreground">Yes, the data is fetched directly by your browser and is never stored on our servers.</p></div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
